/**
 * The notification permission state the app should SHOW, given what the push plugin says and what Android says.
 *
 * Below Android 13 there is no runtime notification permission, and @capacitor-firebase/messaging answers "granted"
 * unconditionally there — it never asks the OS. A phone whose notifications for this app were switched off in
 * Settings (or shipped off by the OEM: MIUI, ColorOS, vivo and Realme often do this for apps installed outside the
 * Play Store) therefore looked fully enabled: no warning, no route to Settings, while Android hid every
 * notification. Production, Sep 2026: about 1 in 5 Witty Android devices were on Android 9–12.
 *
 * ``os`` is the native AppSettings.areNotificationsEnabled() answer — ``{enabled, sdk}`` — or null when it isn't
 * available (web, iOS, or an APK built before the method existed). Rules:
 *   - anything the plugin didn't call "granted" passes through untouched;
 *   - Android 13+ passes through untouched — there the plugin reads the real POST_NOTIFICATIONS permission, which
 *     the OS keeps in step with the app's notification switch;
 *   - only "granted" on Android < 13 with the OS reporting notifications off becomes "denied", so the existing
 *     "blocked → open settings" screens appear.
 * This is what the UI shows. It never decides whether the device registers for push — that stays on the plugin's
 * own answer, exactly as before.
 */
export const ANDROID_13_SDK = 33;

export function effectivePermissionState(pluginState, os) {
    if (pluginState !== "granted") return pluginState;
    if (!os || typeof os.sdk !== "number" || os.sdk >= ANDROID_13_SDK) return pluginState;
    return os.enabled === false ? "denied" : pluginState;
}

// Warning states: the banner/card is on screen and returning from Settings should re-check.
const NEEDS_ATTENTION = new Set(["prompt", "prompt-with-rationale", "denied", "unregistered"]);

export function needsPermissionRecheck(state) {
    return NEEDS_ATTENTION.has(state);
}
